"use client";

import { c as _c } from "react/compiler-runtime";
import { useT, useGameLocale } from "@/lib/i18n";
import { useEffect, useState } from "react";
import { Link } from "@/i18n/navigation";
import { useBetaPrefix } from "@/lib/use-lang-prefix";
const API = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";
interface SimilarItem {
  run_hash: string;
  ascension: number;
  run_time: number;
  username: string | null;
  date: string;
  similarity: number;
}
interface AlsoTook {
  id: string;
  etype: "cards" | "relics";
  name: string;
  count: number;
}
interface SimilarResponse {
  available: boolean;
  items: SimilarItem[];
  winners_also_took: AlsoTook[];
  archetype?: {
    name: string;
    win_rate: number;
    share: number;
    similarity: number;
  } | null;
}
function formatRunTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s.toString().padStart(2, "0")}s` : `${s}s`;
}
export default function SimilarRuns(t0) {
  const $ = _c(33);
  const {
    hash
  } = t0;
  const lang = useGameLocale();
  const t = useT();
  const bp = useBetaPrefix();
  const [data, setData] = useState(null);
  let t1;
  let t2;
  if ($[0] !== hash || $[1] !== lang) {
    t1 = () => {
      let active = true;
      fetch(`${API}/api/runs/${hash}/similar?lang=${lang}`).then(_temp).then(d => {
        if (active && d?.available && d.items?.length) {
          setData(d);
        }
      }).catch(_temp2);
      return () => {
        active = false;
      };
    };
    t2 = [hash, lang];
    $[0] = hash;
    $[1] = lang;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  if (!data) {
    return null;
  }
  let t3;
  if ($[4] !== t) {
    t3 = t("Similar winning runs");
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-1">{t3}</h2>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t) {
    t5 = t("The closest winning decks to this one, by cards and relics.");
    $[8] = t;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t5) {
    t6 = <p className="text-xs text-[var(--text-muted)] mb-3">{t5}</p>;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== bp || $[13] !== data.archetype || $[14] !== t) {
    t7 = data.archetype && <p className="text-xs mb-3"><span className="text-[var(--text-muted)]">{t("Build")}: </span><Link href={`${bp}/archetypes`} className="text-[var(--accent-gold)] hover:underline font-semibold">{data.archetype.name}</Link><span className="text-[var(--text-muted)]">{" "}· {data.archetype.win_rate}% {t("win rate")} ·{" "}{data.archetype.share}% {t("of runs")}</span></p>;
    $[12] = bp;
    $[13] = data.archetype;
    $[14] = t;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== bp || $[17] !== data.items) {
    let t9;
    if ($[19] !== bp) {
      t9 = it => <Link key={it.run_hash} href={`${bp}/runs/${it.run_hash}`} className="flex items-center gap-3 py-1.5 px-2 rounded-md border border-[var(--border-subtle)] hover:border-[var(--border-accent)] transition-colors text-xs"><span className="font-mono text-[var(--accent-gold)] w-12 flex-shrink-0">{it.similarity}%</span><span className="text-[var(--text-secondary)]">A{it.ascension} · {formatRunTime(it.run_time)}</span><span className="text-[var(--text-muted)] truncate">{it.username ?? "anon"}</span><span className="text-[var(--text-muted)] ml-auto flex-shrink-0">{it.date}</span></Link>;
      $[19] = bp;
      $[20] = t9;
    } else {
      t9 = $[20];
    }
    t8 = data.items.map(t9);
    $[16] = bp;
    $[17] = data.items;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[21] !== t8) {
    t9 = <div className="space-y-1.5">{t8}</div>;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== bp || $[24] !== data.winners_also_took || $[25] !== t) {
    t10 = data.winners_also_took.length > 0 && <div className="mt-4"><div className="text-xs font-semibold text-[var(--text-secondary)] mb-2">{t("Winners with decks like this also took")}:</div><div className="flex flex-wrap gap-1.5">{data.winners_also_took.map(w => <Link prefetch={false} key={`${w.etype}-${w.id}`} href={`${bp}/${w.etype}/${w.id.toLowerCase()}`} className="text-xs px-2.5 py-1 rounded-md border border-[var(--border-subtle)] bg-[var(--bg-primary)] text-[var(--text-secondary)] hover:border-[var(--accent-gold)]/50 hover:text-[var(--accent-gold)] transition-colors">{w.name}</Link>)}</div></div>;
    $[23] = bp;
    $[24] = data.winners_also_took;
    $[25] = t;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t10 || $[28] !== t4 || $[29] !== t6 || $[30] !== t7 || $[31] !== t9) {
    t11 = <div className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-subtle)] p-5">{t4}{t6}{t7}{t9}{t10}</div>;
    $[27] = t10;
    $[28] = t4;
    $[29] = t6;
    $[30] = t7;
    $[31] = t9;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  return t11;
}
function _temp2() {}
function _temp(r) {
  return r.ok ? r.json() : null;
}